import {
  AdvancedDataGrid,
  Avatar,
  DataGridRef,
  ListItemProps,
  NestedDataCardProps,
  SelectedFilters,
  Typography,
  createDataCardInitialColumnDefinition,
} from "@inductiveautomation/ws-components";
import { TFunction } from "i18next";
import { snakeCase } from "lodash";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import IgnitionModuleLogo from "../../../../assets/images/modules/ignition.png";
import { BaseLicenseModule, LicenseModule } from "../../Licenses.types";
import { useDemo } from "../../mocks/useDemo";
import { getLicenseStyles } from "../_licenseStyle";
import { matchesStringArrayFilter } from "../utilities";
import ModulesDrawer from "./Modules.Drawer";

export const MODULE_DETAILS_URL =
  "https://inductiveautomation.com/ignition/modules";

const Modules = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { classes } = getLicenseStyles();

  const { mockDataForLicense, mockIgnitionModule } = useDemo();
  const license = mockDataForLicense(id ?? "maker-license-1");

  const dataGridRef = useRef<DataGridRef>(null);
  const [activeModule, setActiveModule] = useState<LicenseModule>();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>();

  const modules: (LicenseModule & {
    type: "ignition" | "core" | "third_party";
  })[] = [
    {
      ...mockIgnitionModule,
      type: "ignition",
    },
    ...license.coreModules.map((module) => ({
      ...module,
      type: "core" as const,
    })),
    ...license.thirdPartyModules.map((module) => ({
      ...module,
      type: "third_party" as const,
    })),
  ];

  const moduleVersions = modules.reduce<string[]>((total, module) => {
    if (total.includes(module.currentVersion)) {
      return total;
    } else {
      return [...total, module.currentVersion];
    }
  }, []);

  const moduleDevelopers = modules.reduce<DeveloperOption[]>((total, module) => {
    const key = snakeCase(module.developer);
    if (!isDeveloperOption(key) || total.includes(key)) return total;
    return [...total, key];
  }, []);

  const filteredData = modules.filter((module) => {
    const developerMatches = matchesStringArrayFilter(
      selectedFilters?.["filter-developer"],
      `developer-${snakeCase(module.developer)}`
    );
    const versionMatches = matchesStringArrayFilter(
      selectedFilters?.["filter-version"],
      `version-${module.currentVersion}`
    );

    return developerMatches && versionMatches;
  });

  return (
    <div className={classes.dataGridContainer}>
      <AdvancedDataGrid
        ref={dataGridRef}
        multiSelect={false}
        chromeless
        testId="maker-license-modules"
        headings={[
          {
            id: "maker-license-modules-heading-details",
            label: t("maker_license.tabs.modules.data_grid.headings.details"),
          },
          {
            id: "maker-license-modules-heading-version",
            label: t("maker_license.tabs.modules.data_grid.headings.version"),
          },
        ]}
        data={filteredData.map(moduleToDataCardMapper(t))}
        filterChips={[
          {
            id: "filter-developer",
            label: t(
              "maker_license.tabs.modules.data_grid.filters.developer.label"
            ),
            variant: "list_advanced",
            listItems: [
              moduleDevelopers.map((developer) =>
                developerFilterItem(t, developer)
              ),
            ],
          },
          {
            id: "filter-version",
            label: t("maker_license.tabs.modules.data_grid.filters.version"),
            variant: "list_advanced",
            listItems: [
              moduleVersions.map((version) => ({
                id: `version-${version}`,
                label: version,
                variant: "checkbox" as const,
              })),
            ],
          },
        ]}
        onSelection={(rowId) => {
          const module = modules.find((module) => module.name === rowId);
          setActiveModule(module);
        }}
        selectedFilters={selectedFilters}
        onFiltersUpdated={setSelectedFilters}
        //groupRowsBy={groupLicenseRows}
        generateCsvData={() => ({
          headings: [
            t("maker_license.tabs.modules.data_grid.fields.name"),
            t("maker_license.tabs.modules.data_grid.fields.description"),
            t("maker_license.tabs.modules.data_grid.headings.version"),
          ],
          rows: filteredData.flatMap((module) => [
            moduleToCsv(module),
            //...module.subModules.map(moduleToCsv),
          ]),
        })}
        labels={{
          singular_item_name: t(
            "maker_license.tabs.modules.data_grid.singular_item_name"
          ),
          plural_item_name: t(
            "maker_license.tabs.modules.data_grid.plural_item_name"
          ),
        }}
      />

      <ModulesDrawer
        module={activeModule}
        onClose={() => {
          setActiveModule(undefined);
          dataGridRef.current?.clearCheckedAndSelected();
        }}
      />
    </div>
  );
};

export default Modules;

const moduleToCsv = (module: BaseLicenseModule) => [
  module.name,
  module.description,
  module.currentVersion,
];

const moduleToDataCardMapper =
  (t: TFunction) =>
  (module: LicenseModule): NestedDataCardProps => ({
    ...singleModule(t, module),
    childItems: [],
    // module.subModules.map((submodule) =>
    //   singleModule(t, submodule),
    // ),
  });

const singleModule = (
  t: TFunction,
  module: BaseLicenseModule
): NestedDataCardProps => {
  const id = module.name;
  return {
    id,
    variant: "default",
    contentColumns: [
      createDataCardInitialColumnDefinition({
        parentTestId: id,
        avatar: (
          <Avatar
            variant="image"
            imageUrl={module.logoUrl || IgnitionModuleLogo}
          />
        ),
        heading: module.name,
        subtext: module.description,
      }),
      {
        content: (
          <Typography
            variant="body2"
            data-testid={`${snakeCase(module.name)}-version`}
          >
            {module.currentVersion}
          </Typography>
        ),
      },
    ],
    actionLists: [
      [
        {
          id: "view-product-details",
          label: t("maker_license.tabs.modules.actions.view_product_details"),
          iconLeft: "visibility",
          iconRight: "open-small",
          iconRightVariant: "neutralLight",
          onClick: () => {
            window.open(MODULE_DETAILS_URL);
          },
        },
      ],
    ],
    childItems: [],
  };
};

// const groupLicenseRows = (row: NestedDataCardProps) => {
//   if (!row.contentColumns || row.contentColumns.length < 1) return "";
//
//   const firstColumn = row.contentColumns[0];
//   if (!isPrimaryColumn(firstColumn)) return "";
//
//   if (firstColumn.content.subtext) {
//     if (firstColumn.content.subtext.startsWith("com.cirruslink.")) {
//       return "Third-party modules";
//     } else {
//       return "Core modules";
//     }
//   } else {
//     return "";
//   }
// };

const DEVELOPER_OPTIONS = [
  "cirrus_link",
  "inductive_automation",
  "sepasoft",
  "third_party",
] as const;
type DeveloperOption = (typeof DEVELOPER_OPTIONS)[number];

const isDeveloperOption = (key: string): key is DeveloperOption =>
  (DEVELOPER_OPTIONS as readonly string[]).includes(key);

const developerFilterItem = (
  t: TFunction,
  key: DeveloperOption
): ListItemProps => ({
  id: `developer-${key}`,
  label: t(
    `maker_license.tabs.modules.data_grid.filters.developer.options.${key}`
  ),
  variant: "checkbox" as const,
});
